import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Dialog from '@mui/material/Dialog'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { labourApi, useContractors, useLabourBulk, type LabourEntry } from '../api/execution'
import { useLabourCategories } from '../api/hooks'
import { useState } from 'react'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { ActualAgainstPlan, ScopeChip } from '../components/StatusChip'
import { TemplateBar } from '../components/TemplateBar'
import { DayEntryShell } from './entry/DayEntryShell'
import { LabourEditForm } from './report/LabourEditForm'
import { LabourQuickForm } from './report/LabourQuickForm'

/** Direct route to labour entry: pick a site and date, type the headcounts, save. */
export function LabourEntryPage() {
  return (
    <DayEntryShell
      title="Labour worked"
      subtitle="Record the trades and headcounts on site for a day. Mason 10, Plumber 10, Helper 15."
    >
      {(report, editable) => <LabourEntryBody reportId={report.id} editable={editable} />}
    </DayEntryShell>
  )
}

function LabourEntryBody({ reportId, editable }: { reportId: string; editable: boolean }) {
  const list = labourApi.useList(reportId)
  const update = labourApi.useUpdate(reportId)
  const remove = labourApi.useDelete(reportId)
  const bulk = useLabourBulk(reportId)
  const categories = useLabourCategories()
  const contractors = useContractors()
  const [editing, setEditing] = useState<LabourEntry | null>(null)

  const rows = list.data ?? []
  const totals = rows.reduce(
    (acc, l: LabourEntry) => ({
      workers: acc.workers + (l.totalWorkers ?? 0),
      own: acc.own + (l.labourScope === 'OWN' ? (l.totalWorkers ?? 0) : 0),
      manHours: acc.manHours + Number(l.manHours ?? 0),
    }),
    { workers: 0, own: 0, manHours: 0 },
  )
  // Lines an applied template put there, still waiting for the muster count.
  const awaitingActuals = rows.filter(
    (l) => (l.plannedWorkers ?? 0) > 0 && (l.totalWorkers ?? 0) === 0,
  ).length

  return (
    <>
      <TemplateBar module="LABOUR" reportId={reportId} editable={editable} />

      {editable && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <LabourQuickForm
            categories={(categories.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
            contractors={(contractors.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
            pending={bulk.isPending}
            error={bulk.error}
            inline
            onCancel={() => undefined}
            onSave={async (quickRows) => {
              await bulk.mutateAsync(quickRows)
            }}
          />
        </Paper>
      )}

      <Typography variant="h6" sx={{ mb: 1 }}>
        Recorded for this day
      </Typography>
      {list.isLoading && <LoadingBlock />}
      {list.error && <ErrorBlock error={list.error} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Labours</TableCell>
              <TableCell>Whose labour</TableCell>
              <TableCell align="right">Planned</TableCell>
              <TableCell align="right">Male</TableCell>
              <TableCell align="right">Female</TableCell>
              <TableCell align="right">Skilled</TableCell>
              <TableCell align="right">Semi</TableCell>
              <TableCell align="right">Unskilled</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">OT</TableCell>
              <TableCell align="right">Man-hours</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((l) => (
              <TableRow key={l.id} hover>
                <TableCell>{l.labourCategoryName}</TableCell>
                <TableCell>
                  <ScopeChip scope={l.labourScope} party={l.contractorName} />
                </TableCell>
                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                  {l.plannedWorkers ?? '-'}
                </TableCell>
                <TableCell align="right">{l.maleCount}</TableCell>
                <TableCell align="right">{l.femaleCount}</TableCell>
                <TableCell align="right">{l.skilledCount}</TableCell>
                <TableCell align="right">{l.semiSkilledCount}</TableCell>
                <TableCell align="right">{l.unskilledCount}</TableCell>
                <TableCell align="right">
                  <ActualAgainstPlan actual={l.totalWorkers ?? 0} planned={l.plannedWorkers} />
                </TableCell>
                <TableCell align="right">{l.workingHours}</TableCell>
                <TableCell align="right">{l.overtimeHours}</TableCell>
                <TableCell align="right">{l.manHours}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {editable && (
                    <>
                      <IconButton size="small" title="Edit" onClick={() => setEditing(l)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Remove"
                        onClick={() => remove.mutate(l.id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No labour recorded for this day yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {awaitingActuals > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {awaitingActuals} line(s) carry a planned headcount with nothing entered against
          it yet. Edit each one with the gang that actually turned up.
        </Alert>
      )}

      {rows.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <strong>{totals.workers}</strong> workers on site — <strong>{totals.own}</strong> own,{' '}
          <strong>{totals.workers - totals.own}</strong> contractor-supplied ·{' '}
          <strong>{totals.manHours.toFixed(1)}</strong> man-hours across {rows.length} trade(s).
        </Alert>
      )}

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        {editing && (
          <LabourEditForm
            entry={editing}
            contractors={(contractors.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
            pending={update.isPending}
            onCancel={() => setEditing(null)}
            onSave={async (body) => {
              await update.mutateAsync({ id: editing.id, body })
              setEditing(null)
            }}
          />
        )}
      </Dialog>

      <Box sx={{ height: 24 }} />
    </>
  )
}
